/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable @typescript-eslint/no-non-null-assertion */
import { Button, Flex, useDisclosure } from '@chakra-ui/react';
import { useMemo, useState, type FC } from 'react';
import { type Frame, type Request } from '../../interface/Crash';
import { type Protocol } from '../../interface/Protocol';
import { DetailsDrawer } from '../detailsDrawer/DetailsDrawer';
import { RequestDetail } from '../requestDetail/RequestDetail';
import { BKTraceTable } from './BKTraceTable';
import { Collections } from './Collections';
import { JsiDetail } from '../requestDetail/JsiDetail';

interface Props {
    protocolId: string;
    details: Protocol;
}

export interface ProcessedTrace {
    created: string;
    sessionID: string;
    sessionIdIndex: number;
    traceIndex: number;
    message?: string;
    messageOffsetMs?: number;
    requests?: Request[];
    callstack?: Frame[];
    fileVersion?: string;
    lineNumber?: number;
}

export const BKTrace: FC<Props> = (props: Props) => {
    const { protocolId, details } = props;

    const [selected, setSelected] = useState<ProcessedTrace | null>(null);
    const [showUserTimezone, setShowUserTimezone] = useState(false);
    const { isOpen: isOpenDetails, onOpen: onOpenDetails, onClose: onCloseDetails } = useDisclosure();
    const selectedRequest = selected?.requests?.[0];

    const handleSelect = (trace: ProcessedTrace): void => {
        setSelected(trace);
        if (trace.requests?.[0]) {
            onOpenDetails();
        }
    };

    const sessionIDs = useMemo(
        () =>
            details.trace.reduce<string[]>((accumulator, current) => {
                return accumulator.includes(current['session-id'])
                    ? accumulator
                    : [...accumulator, current['session-id']];
            }, []),
        [details.trace],
    );

    const getSessionIdIndex = (sessionId: string): number => sessionIDs.indexOf(sessionId);

    const [proceccedTrace, detectedTzOffsetMs] = useMemo(() => {
        const trace = details.trace.map((item, index) => {
            const sid = item['session-id'];
            const { message, messageOffsetMs } = stripMessageTimestamp(item.message, item.created);
            const processed: ProcessedTrace = {
                created: item.created,
                sessionID: sid,
                sessionIdIndex: getSessionIdIndex(sid),
                traceIndex: index,
                message,
                messageOffsetMs,
            };
            if (item.body) {
                const [requests, callstack] = spliceRequestsFromCallstack(item.body.frames);
                return {
                    ...processed,
                    requests,
                    callstack,
                    fileVersion: item.body.fileVersion,
                    lineNumber: item.body.lineNumber,
                };
            }
            return processed;
        });

        const tzOffsetMs = detectTimezoneOffset(trace);
        if (tzOffsetMs !== 0) {
            for (const item of trace) {
                if (item.messageOffsetMs !== undefined) {
                    item.messageOffsetMs -= tzOffsetMs;
                }
            }
        }
        return [trace, tzOffsetMs] as const;
    }, [details.trace, sessionIDs]);

    const RequestViewerComponent = selectedRequest?.type === 'JSI' ? JsiDetail : RequestDetail;

    return (
        <>
            <Flex gap={6} alignItems="center">
                <Collections protocolId={protocolId} details={details} sessionIDs={sessionIDs} />
                <Button
                    size="sm"
                    colorScheme="blue"
                    variant={showUserTimezone ? 'solid' : 'outline'}
                    onClick={() => {
                        setShowUserTimezone((prev) => !prev);
                    }}
                >
                    {showUserTimezone ? formatTzOffset(detectedTzOffsetMs) : 'UTC'}
                </Button>
            </Flex>
            <BKTraceTable
                trace={proceccedTrace}
                setSelected={handleSelect}
                tzOffsetMs={showUserTimezone ? detectedTzOffsetMs : 0}
                tzLabel={showUserTimezone ? formatTzOffset(detectedTzOffsetMs) : 'UTC'}
            />
            <DetailsDrawer isOpen={isOpenDetails} size="full" onClose={onCloseDetails} title="">
                {selectedRequest && (
                    <RequestViewerComponent
                        request={selectedRequest}
                        callstack={selected.callstack}
                        reportKind="protocol"
                        reportId={protocolId}
                        requestId={selected.traceIndex}
                        fileVersion={selected.fileVersion}
                        lineNumber={selected.lineNumber}
                    />
                )}
            </DetailsDrawer>
        </>
    );
};

const spliceRequestsFromCallstack = (frames: Frame[]): [Request[], Frame[]] =>
    frames.reduce<[Request[], Frame[]]>(
        (acc, item) => {
            if (item.name === 'requests' && item.bindings.requests && !item.source) {
                acc[0].push(item.bindings.requests[1]);
            } else {
                acc[1].push(item);
            }
            return acc;
        },
        [[], []],
    );

const MESSAGE_TIMESTAMP_RE = /^(\d{4}-\d{2}-\d{2}) (\d{2}):(\d{2}):(\d{2})\.(\d+): ([\s\S]*)$/;

const stripMessageTimestamp = (
    message: string | undefined,
    created: string,
): { message?: string; messageOffsetMs?: number } => {
    if (!message) return {};
    const match = message.match(MESSAGE_TIMESTAMP_RE);
    if (!match) return { message };
    const [, datePart, h, m, s, frac, rest] = match;
    const ms = frac.padEnd(3, '0').slice(0, 3);
    const messageMs = Date.parse(`${datePart}T${h}:${m}:${s}.${ms}`);
    const createdMatch = created.match(/^(\d{4}-\d{2}-\d{2})T(\d{2}):(\d{2}):(\d{2})\.(\d+)/);
    if (!createdMatch || isNaN(messageMs)) return { message: rest };
    const [, cd, ch, cm, cs, cfrac] = createdMatch;
    const cms = cfrac.padEnd(3, '0').slice(0, 3);
    const createdMs = Date.parse(`${cd}T${ch}:${cm}:${cs}.${cms}`);
    return {
        message: rest,
        messageOffsetMs: messageMs - createdMs,
    };
};

const HALF_HOUR_MS = 1_800_000;

const detectTimezoneOffset = (trace: ProcessedTrace[]): number => {
    const counts = new Map<number, number>();
    for (const item of trace) {
        if (item.messageOffsetMs === undefined) continue;
        const rounded = Math.round(item.messageOffsetMs / HALF_HOUR_MS) * HALF_HOUR_MS;
        counts.set(rounded, (counts.get(rounded) ?? 0) + 1);
    }
    let maxCount = 0;
    let offset = 0;
    counts.forEach((count, key) => {
        if (count > maxCount) {
            maxCount = count;
            offset = key;
        }
    });
    return offset;
};

const formatTzOffset = (offsetMs: number): string => {
    const totalMinutes = Math.round(offsetMs / 60000);
    const hours = Math.trunc(totalMinutes / 60);
    const minutes = Math.abs(totalMinutes % 60);
    const sign = hours >= 0 ? '+' : '';
    if (minutes === 0) return `UTC${sign}${hours}`;
    return `UTC${sign}${hours}:${minutes.toString().padStart(2, '0')}`;
};
